/**
 * コンテンツ配信レイヤ(docs/04_data_model.md §3)。
 *
 * `content/v1/` の静的JSON(tools/content-build の成果物)を読み、
 * ステージ定義と単語を組み立てて engine の `StageWord[]` に変換する。
 * MVP では Vite が `/content/` として同一オリジンで配信し、将来 R2+CDN に差し替える。
 */

import type { QuizConfig, QuizMode, StageWord } from '@paltyping/engine';
import { buildCatalog, type Catalog } from './catalog';
import { DEFAULT_FIRST_LANGUAGE, type LanguageCode } from './languages';

/** ゲームモード(= コンテンツセットの違い。docs/03 §7)。 */
export type Language = 'japanese' | 'english';
/** ステージのカテゴリタブ。 */
export type StageCategory = 'practice' | 'training';

export interface WordMedia {
  /** 代表アセット(後方互換。`images`/`audios` が無い語はこちらを使う)。 */
  readonly image?: string;
  readonly audio?: { readonly en?: string; readonly ja?: string };
  /** 多様提示バリエーション: 画像スタイル4種(docs/05 §2b)。 */
  readonly images?: readonly string[];
  /** 多様提示バリエーション: 英語音声の話者4種(docs/05 §3b)。 */
  readonly audios?: { readonly en?: readonly string[]; readonly ja?: readonly string[] };
}

/** `content/v1/words/{set}.json` の1要素。 */
export interface ContentWord {
  readonly id: string;
  readonly display: string;
  readonly reading: string;
  readonly ruby?: string;
  readonly meaning?: Readonly<Record<string, string>>;
  readonly media?: WordMedia;
  readonly tags?: readonly string[];
}

/**
 * 学習者の第一言語(訳を出すときの言語)。
 *
 * 設定の言語コード(`services/languages` の `LanguageCode`)と同じもの。
 * 実際の値は「ことばの せってい」から来る。
 */
export type FirstLanguage = LanguageCode;

/** 訳のフォールバック先(コンテンツは ja の訳を必ず持つ)。 */
export { DEFAULT_FIRST_LANGUAGE } from './languages';

/**
 * 第一言語の訳を取り出す(docs/02 §2 の英語モードの訳表示)。
 *
 * 第一言語の訳が無ければ日本語 → 収録されている最初の訳、の順にフォールバックする
 * (訳が1つも無い語だけ null)。
 */
export function meaningInFirstLanguage(
  meaning: Readonly<Record<string, string>> | undefined,
  firstLanguage: FirstLanguage = DEFAULT_FIRST_LANGUAGE,
): string | null {
  if (!meaning) return null;
  const preferred = meaning[firstLanguage];
  if (preferred) return preferred;
  if (meaning[DEFAULT_FIRST_LANGUAGE]) return meaning[DEFAULT_FIRST_LANGUAGE];
  const first = Object.values(meaning).find((value) => value.length > 0);
  return first ?? null;
}

export interface WordSetFile {
  readonly schemaVersion: number;
  readonly set: string;
  readonly locale: string;
  readonly words: readonly ContentWord[];
}

export interface StageUnlock {
  readonly requires: readonly string[];
  readonly minStars: number;
}

/** `content/v1/stages/{language}.json` の1要素。 */
export interface StageDefinition {
  readonly id: string;
  readonly language: Language;
  readonly category: StageCategory;
  readonly section: string;
  readonly title: string;
  readonly titleSub?: string;
  readonly wordIds: readonly string[];
  readonly quiz: QuizConfig;
  readonly viewImage: boolean;
  readonly voice: boolean;
  /** 星1〜5の初回獲得ボーナスコイン。 */
  readonly starCoins: readonly number[];
  readonly unlock?: StageUnlock;
  readonly gameType: string;
}

export interface StageSetFile {
  readonly schemaVersion: number;
  readonly language: Language;
  readonly wordSets: readonly string[];
  readonly stages: readonly StageDefinition[];
}

/** ステージ1つを遊ぶのに必要な素材一式。 */
export interface StageBundle {
  readonly stage: StageDefinition;
  /** engine に渡す出題語。 */
  readonly words: readonly StageWord[];
  /** 画像・音声・意味の引き当て用(id → コンテンツ単語)。 */
  readonly wordsById: ReadonlyMap<string, ContentWord>;
}

/** コンテンツの読み込み・スキーマ違反。 */
export class ContentError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ContentError';
  }
}

// ── パース ────────────────────────────────────────────────

function asRecord(value: unknown, where: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new ContentError(`${where}: オブジェクトではありません`);
  }
  return value as Record<string, unknown>;
}

function asString(value: unknown, where: string): string {
  if (typeof value !== 'string' || value.length === 0) {
    throw new ContentError(`${where}: 文字列ではありません`);
  }
  return value;
}

function asArray(value: unknown, where: string): unknown[] {
  if (!Array.isArray(value)) throw new ContentError(`${where}: 配列ではありません`);
  return value;
}

function optionalString(value: unknown, where: string): string | undefined {
  if (value === undefined || value === null) return undefined;
  return asString(value, where);
}

function optionalStringArray(value: unknown, where: string): readonly string[] | undefined {
  if (value === undefined || value === null) return undefined;
  return asArray(value, where).map((entry, index) => asString(entry, `${where}[${index}]`));
}

const QUIZ_MODES: readonly QuizMode[] = ['sequential', 'random', 'timeLimit', 'fixedQuestionCount'];

const LANGUAGES: readonly Language[] = ['japanese', 'english'];
const CATEGORIES: readonly StageCategory[] = ['practice', 'training'];

function parseQuiz(value: unknown, where: string): QuizConfig {
  const raw = asRecord(value, where);
  const mode = asString(raw.mode, `${where}.mode`);
  if (!(QUIZ_MODES as readonly string[]).includes(mode)) {
    throw new ContentError(`${where}.mode: 未知の出題モード "${mode}"`);
  }
  const numeric = (key: 'repeatCount' | 'timeLimit' | 'questionLimit'): number | undefined => {
    const v = raw[key];
    if (v === undefined || v === null) return undefined;
    if (typeof v !== 'number' || !Number.isFinite(v)) {
      throw new ContentError(`${where}.${key}: 数値ではありません`);
    }
    return v;
  };
  const repeatCount = numeric('repeatCount');
  const timeLimit = numeric('timeLimit');
  const questionLimit = numeric('questionLimit');
  return {
    mode: mode as QuizMode,
    ...(repeatCount === undefined ? {} : { repeatCount }),
    ...(timeLimit === undefined ? {} : { timeLimit }),
    ...(questionLimit === undefined ? {} : { questionLimit }),
  };
}

function parseMedia(value: unknown, where: string): WordMedia | undefined {
  if (value === undefined || value === null) return undefined;
  const raw = asRecord(value, where);
  const image = optionalString(raw.image, `${where}.image`);
  let audio: { en?: string; ja?: string } | undefined;
  if (raw.audio !== undefined && raw.audio !== null) {
    const rawAudio = asRecord(raw.audio, `${where}.audio`);
    const en = optionalString(rawAudio.en, `${where}.audio.en`);
    const ja = optionalString(rawAudio.ja, `${where}.audio.ja`);
    audio = { ...(en === undefined ? {} : { en }), ...(ja === undefined ? {} : { ja }) };
  }
  // バリエーション(docs/04 §3)。旧データには無いので optional。
  const images = optionalStringArray(raw.images, `${where}.images`);
  let audios: { en?: readonly string[]; ja?: readonly string[] } | undefined;
  if (raw.audios !== undefined && raw.audios !== null) {
    const rawAudios = asRecord(raw.audios, `${where}.audios`);
    const en = optionalStringArray(rawAudios.en, `${where}.audios.en`);
    const ja = optionalStringArray(rawAudios.ja, `${where}.audios.ja`);
    audios = { ...(en === undefined ? {} : { en }), ...(ja === undefined ? {} : { ja }) };
  }
  return {
    ...(image === undefined ? {} : { image }),
    ...(audio === undefined ? {} : { audio }),
    ...(images === undefined ? {} : { images }),
    ...(audios === undefined ? {} : { audios }),
  };
}

/** `words/{set}.json` をパースする。 */
export function parseWordSet(raw: unknown): WordSetFile {
  const root = asRecord(raw, 'wordSet');
  const words = asArray(root.words, 'wordSet.words').map((entry, index) => {
    const where = `wordSet.words[${index}]`;
    const w = asRecord(entry, where);
    const media = parseMedia(w.media, `${where}.media`);
    const ruby = optionalString(w.ruby, `${where}.ruby`);
    const meaning =
      w.meaning === undefined || w.meaning === null
        ? undefined
        : (asRecord(w.meaning, `${where}.meaning`) as Record<string, string>);
    const tags = w.tags === undefined ? undefined : asArray(w.tags, `${where}.tags`).map(String);
    const word: ContentWord = {
      id: asString(w.id, `${where}.id`),
      display: asString(w.display, `${where}.display`),
      reading: asString(w.reading, `${where}.reading`),
      ...(ruby === undefined ? {} : { ruby }),
      ...(meaning === undefined ? {} : { meaning }),
      ...(media === undefined ? {} : { media }),
      ...(tags === undefined ? {} : { tags }),
    };
    return word;
  });
  return {
    schemaVersion: typeof root.schemaVersion === 'number' ? root.schemaVersion : 0,
    set: asString(root.set, 'wordSet.set'),
    locale: asString(root.locale, 'wordSet.locale'),
    words,
  };
}

/** `stages/{language}.json` をパースする。 */
export function parseStageSet(raw: unknown): StageSetFile {
  const root = asRecord(raw, 'stageSet');
  const language = asString(root.language, 'stageSet.language');
  if (!(LANGUAGES as readonly string[]).includes(language)) {
    throw new ContentError(`stageSet.language: 未知の言語 "${language}"`);
  }
  const stages = asArray(root.stages, 'stageSet.stages').map((entry, index) => {
    const where = `stageSet.stages[${index}]`;
    const s = asRecord(entry, where);
    const category = asString(s.category, `${where}.category`);
    if (!(CATEGORIES as readonly string[]).includes(category)) {
      throw new ContentError(`${where}.category: 未知のカテゴリ "${category}"`);
    }
    const titleSub = optionalString(s.titleSub, `${where}.titleSub`);
    const starCoins = asArray(s.starCoins, `${where}.starCoins`).map((n) =>
      typeof n === 'number' && Number.isFinite(n) ? n : 0,
    );
    let unlock: StageUnlock | undefined;
    if (s.unlock !== undefined && s.unlock !== null) {
      const u = asRecord(s.unlock, `${where}.unlock`);
      unlock = {
        requires: asArray(u.requires ?? [], `${where}.unlock.requires`).map(String),
        minStars: typeof u.minStars === 'number' ? u.minStars : 0,
      };
    }
    const stage: StageDefinition = {
      id: asString(s.id, `${where}.id`),
      language: asString(s.language, `${where}.language`) as Language,
      category: category as StageCategory,
      section: asString(s.section, `${where}.section`),
      title: asString(s.title, `${where}.title`),
      ...(titleSub === undefined ? {} : { titleSub }),
      wordIds: asArray(s.wordIds, `${where}.wordIds`).map(String),
      quiz: parseQuiz(s.quiz, `${where}.quiz`),
      viewImage: s.viewImage === true,
      voice: s.voice === true,
      starCoins,
      ...(unlock === undefined ? {} : { unlock }),
      gameType: typeof s.gameType === 'string' ? s.gameType : 'standard',
    };
    return stage;
  });
  return {
    schemaVersion: typeof root.schemaVersion === 'number' ? root.schemaVersion : 0,
    language: language as Language,
    wordSets: asArray(root.wordSets ?? [], 'stageSet.wordSets').map(String),
    stages,
  };
}

/** コンテンツ単語を engine の出題語に変換する。 */
export function toStageWord(word: ContentWord): StageWord {
  const image = word.media?.image;
  return {
    id: word.id,
    display: word.display,
    reading: word.reading,
    ...(word.ruby === undefined ? {} : { ruby: word.ruby }),
    ...(image === undefined ? {} : { media: image }),
  };
}

/** ステージ定義と単語辞書から出題素材を組み立てる。未知IDは無視せずエラーにする。 */
export function buildStageBundle(
  stage: StageDefinition,
  dictionary: ReadonlyMap<string, ContentWord>,
): StageBundle {
  const wordsById = new Map<string, ContentWord>();
  const words: StageWord[] = [];
  for (const id of stage.wordIds) {
    const word = dictionary.get(id);
    if (!word) throw new ContentError(`ステージ ${stage.id}: 単語 ${id} が見つかりません`);
    wordsById.set(id, word);
    words.push(toStageWord(word));
  }
  if (words.length === 0) throw new ContentError(`ステージ ${stage.id}: 出題語がありません`);
  return { stage, words, wordsById };
}

// ── サービス ──────────────────────────────────────────────

export type FetchLike = (
  input: string,
) => Promise<{ ok: boolean; status: number; json(): Promise<unknown> }>;

export interface ContentServiceOptions {
  /** 既定 `/content/v1/`。 */
  readonly baseUrl?: string;
  readonly fetchFn?: FetchLike;
}

export class ContentService {
  private readonly baseUrl: string;
  private readonly fetchFn: FetchLike;
  private readonly stageCache = new Map<Language, Promise<StageSetFile>>();
  private readonly wordCache = new Map<string, Promise<ReadonlyMap<string, ContentWord>>>();
  private catalogCache: Promise<Catalog> | null = null;

  constructor(options: ContentServiceOptions = {}) {
    this.baseUrl = options.baseUrl ?? '/content/v1/';
    this.fetchFn = options.fetchFn ?? ((url) => fetch(url));
  }

  private async getJson(relative: string): Promise<unknown> {
    const url = `${this.baseUrl}${relative}`;
    let response;
    try {
      response = await this.fetchFn(url);
    } catch (cause) {
      throw new ContentError(`${url} の取得に失敗しました: ${String(cause)}`);
    }
    if (!response.ok)
      throw new ContentError(`${url} の取得に失敗しました (HTTP ${response.status})`);
    return response.json();
  }

  /** ステージ一覧(言語単位)。 */
  loadStages(language: Language): Promise<StageSetFile> {
    const cached = this.stageCache.get(language);
    if (cached) return cached;
    const promise = this.getJson(`stages/${language}.json`)
      .then(parseStageSet)
      .catch((error: unknown) => {
        this.stageCache.delete(language);
        throw error;
      });
    this.stageCache.set(language, promise);
    return promise;
  }

  /** 単語セット(id 引きのMap)。 */
  loadWordSet(set: string): Promise<ReadonlyMap<string, ContentWord>> {
    const cached = this.wordCache.get(set);
    if (cached) return cached;
    const promise = this.getJson(`words/${set}.json`)
      .then((raw) => {
        const parsed = parseWordSet(raw);
        return new Map(parsed.words.map((word) => [word.id, word]));
      })
      .catch((error: unknown) => {
        this.wordCache.delete(set);
        throw error;
      });
    this.wordCache.set(set, promise);
    return promise;
  }

  /** アイテム・ガチャカタログ(アバター/ガチャ画面用)。 */
  loadCatalog(): Promise<Catalog> {
    if (this.catalogCache) return this.catalogCache;
    const promise = Promise.all([
      this.getJson('catalog/items.json'),
      this.getJson('catalog/gachas.json'),
    ])
      .then(([items, gachas]) => buildCatalog(items, gachas))
      .catch((error: unknown) => {
        this.catalogCache = null;
        throw error;
      });
    this.catalogCache = promise;
    return promise;
  }

  /** 1ステージ分の出題素材。 */
  async loadStageBundle(language: Language, stageId: string): Promise<StageBundle> {
    const stageSet = await this.loadStages(language);
    const stage = stageSet.stages.find((s) => s.id === stageId);
    if (!stage) throw new ContentError(`ステージ ${stageId} が見つかりません`);
    const dictionaries = await Promise.all(stageSet.wordSets.map((set) => this.loadWordSet(set)));
    const merged = new Map<string, ContentWord>();
    for (const dictionary of dictionaries) {
      for (const [id, word] of dictionary) merged.set(id, word);
    }
    return buildStageBundle(stage, merged);
  }
}
